import React from 'react';
import { X, Info, CheckCircle2 } from 'lucide-react';

interface TutorialData {
  title: string;
  description: string;
  videoUrl?: string;
  steps?: string[];
}

export const TutorialModal: React.FC<{ 
  isOpen: boolean; 
  onClose: () => void; 
  data: TutorialData | null;
}> = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-6">
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-md animate-fade-in" 
        onClick={onClose} 
      />
      
      <div className="relative w-full max-w-2xl bg-slate-900 border border-white/10 rounded-[32px] shadow-2xl overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="p-6 border-b border-white/5 flex justify-between items-center bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-500/20 rounded-xl">
              <Info className="w-5 h-5 text-indigo-400" />
            </div>
            <h3 className="text-xl font-bold text-white">{data.title}</h3>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/5 rounded-full transition-colors text-slate-500 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar">
          <div className="p-6 bg-indigo-500/5 border border-indigo-500/10 rounded-2xl flex gap-4">
            <Info className="w-6 h-6 text-indigo-400 shrink-0" />
            <p className="text-slate-300 leading-relaxed">{data.description}</p>
          </div>

          {/* Steps */}
          {data.steps && (
            <div className="space-y-4">
              <h4 className="text-sm font-bold text-white uppercase tracking-widest flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                O que fazer agora:
              </h4>
              <div className="space-y-3">
                {data.steps.map((step, idx) => (
                  <div key={idx} className="flex gap-4 p-4 bg-white/[0.02] border border-white/5 rounded-xl hover:bg-white/[0.04] transition-colors">
                    <span className="text-indigo-500 font-black text-lg opacity-40">{idx + 1}</span>
                    <p className="text-sm text-slate-400 leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 bg-white/[0.02] border-t border-white/5 flex justify-end">
          <button 
            onClick={onClose}
            className="px-8 py-3 bg-white text-black font-bold rounded-2xl hover:bg-slate-200 transition-all uppercase tracking-widest text-[12px]"
          >
            Entendi, vamos lá!
          </button>
        </div>
      </div>
    </div>
  );
};
